import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import * as yup from "yup";
import UIkit from "uikit";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";
import GoogleAutoComplete from "../common/GoogleAutoComplete";
import { AppContexts } from "../../providers";
import utils from "../../utils/utils";

const schema = yup.object().shape({
  pms_id: yup.string().required(),
});

const AddPropertyGuide = () => {
  const [placeId, setPlaceId] = useState(null);
  const UtilContext = useContext(AppContexts.UtilContext);
  const { user } = useContext(AppContexts.AuthContext);
  const RentalsContext = useContext(AppContexts.RentalsContext);
  const [isValidate, setValidate] = useState(true);
  const [autoCompleteValue, setAutoCompleteValue] = useState("");
  const history = useHistory();
  const offcanvasRef = useRef(null);
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRegister = async (data) => {
    placeId ? setValidate(true) : setValidate(false);
    if (placeId) {
      try {
        let rental = {};
        if (RentalsContext.isNew) {
          RentalsContext.setUpsertLoading(true);
          let place = await UtilContext.place(placeId);
          rental = {
            destination_id: 0,
            google_place_id: place.place_id,
            host_id: user.account_id,
            pms_id: data.pms_id || "",
            name: data.name || place.name,
            address: place.formatted_address,
            lat: data.lat || place.geometry.location.lat,
            lng: data.lng || place.geometry.location.lng,
            property_code: "",
          };
          rental = await RentalsContext.createRental(rental);
        } else {
          rental = {
            destination_id: 0,
            host_id: user.account_id,
            pms_id: data.pms_id || "",
            name: data.name,
            property_code: "",
          }
          if (placeId !== RentalsContext.rental.google_place_id) {
            RentalsContext.setUpsertLoading(true);
            let place = await UtilContext.place(placeId);
            rental = {
              destination_id: data.destination_id == "" ? 0 : data.destination_id,
              google_place_id: place.place_id,
              host_id: user.account_id,
              pms_id: data.pms_id || "",
              name: data.name || place.name,
              address: place.formatted_address,
              lat: data.lat || place.geometry.location.lat,
              lng: data.lng || place.geometry.location.lng,
              property_code: "",
            };
          }
          rental = await RentalsContext.updateRental(
            RentalsContext.rental.rental_id,
            {
              ...RentalsContext.rental,
              ...rental,
            }
          );
        }
  
        if (!rental) {
          UIkit.notification("Failed", "error");
          RentalsContext.setUpsertLoading(false);
        } else {
          UIkit.modal("#addPropertyGuide-modal").hide();
          UIkit.notification(
            RentalsContext.isNew ? "Added Successfully" : "Updated Successfully",
            "success"
          );
          RentalsContext.setUpsertLoading(false);
  
          RentalsContext.get();
          if (RentalsContext.isNew)
            history.push(`/units`);
          RentalsContext.setIsNew(false);
        }
      } catch (err) {
        RentalsContext.setUpsertLoading(false);
        console.log(err);
      }
    }
  };

  const handleBeforeHide = (e) => {
    if (e.target === offcanvasRef.current) {
      setAutoCompleteValue("");
      setValidate(true);
      reset();
      clearErrors();
      RentalsContext.setRental({});
    }
  };

  useEffect(() => {
    const offcanvasElement = UIkit.offcanvas(offcanvasRef.current);
    const offcanvasDOMElement = offcanvasElement.$el;
    offcanvasDOMElement.addEventListener("beforehide", handleBeforeHide);
    return () => {
      offcanvasDOMElement.removeEventListener("beforehide", handleBeforeHide);
    };
  }, []);

  useEffect(() => {
    if (RentalsContext.isNew) {
      reset();
      setAutoCompleteValue("");
    } else {
      setValue("pms_id", RentalsContext.rental.pms_id);
      setValue("name", RentalsContext.rental.name);
      setValue("destination_id", RentalsContext.rental.destination_id);
      setPlaceId(RentalsContext.rental.google_place_id);
      setAutoCompleteValue(RentalsContext.rental.address);
      setValue('lat', RentalsContext.rental.lat);
      setValue('lng', RentalsContext.rental.lng);
      RentalsContext.getLogs(RentalsContext.rental.rental_id);
    }
  }, [RentalsContext.isNew, RentalsContext.rental]);

  useEffect(() => {
    if (RentalsContext.error)
      setError("pms_id", { type: "custom", message: RentalsContext.error });
  }, [RentalsContext.error]);

  useEffect(() => {
    RentalsContext.setPMSError(null);
  }, [watch("pms_id")]);

  const destination_id = useMemo(
    () => getValues("destination_id"),
    [watch("destination_id")]
  );

  const handleError = (err) => {
    console.log(err);
  };

  return (
    <div
      id="addPropertyGuide-modal"
      className="uk-modal-full hms-modals uk-modal"
      uk-offcanvas="overlay: true; flip: true;"
      ref={offcanvasRef}
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {RentalsContext.isNew ? "Add a" : "Edit"} Unit
            </h2>
          </div>
          <div className="uk-modal-body">
            <form
              className="uk-grid-small"
              uk-grid="true"
              onSubmit={handleSubmit(handleRegister, handleError)}
            >
              <p className="secondary">
                Your subscription is based on the number of units. 
                Adding a unit will increase your subscription cost.
              </p>
              <div className="uk-width-1-1">
                <div className="uk-form-controls">
                  <label className="uk-form-label">Property Address*</label>
                  <GoogleAutoComplete
                    className={classnames({ error: isSubmitted && (!isValidate || !autoCompleteValue) })}
                    width="100%"
                    onSelect={(_, value) => setPlaceId(value.text)}
                    onChange={(e) => setAutoCompleteValue(e)}
                    address={autoCompleteValue}
                  />
                </div>
                <div className="hms-small-itelic">
                  Begin typing and select a suggested address.
                </div>
              </div>
              <div className="uk-width-1-1">
                <div className="uk-form-controls">
                  <label className="uk-form-label">
                    Property Id*{" "}
                    <img
                      className="info-icon"
                      src="/images/info-icon.svg"
                      uk-tooltip="Use the ID assigned in you property management software."
                    />
                  </label>
                  <input
                    className={classnames("uk-input", {
                      error: errors["pms_id"],
                    })}
                    type="text"
                    {...register("pms_id")}
                  />
                  <div className="uk-label-danger">
                    {RentalsContext.error}
                  </div>
                </div>
              </div>
              {!RentalsContext.isNew && <div className="uk-width-1-2@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label">
                    Latitude
                  </label>
                  <input
                    className={classnames("uk-input", {
                      error: errors["name"],
                    })}
                    type="text"
                    {...register("lat")}
                  />
                </div>
              </div>}
              {!RentalsContext.isNew && <div className="uk-width-1-2@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label">
                    Longitude
                  </label>
                  <input
                    className={classnames("uk-input", {
                      error: errors["name"],
                    })}
                    type="text"
                    {...register("lng")}
                  />
                </div>
              </div>}
              <div>
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="submit"
                  disabled={RentalsContext.upsertLoading}
                >
                  {RentalsContext.upsertLoading ? (
                    <div data-uk-spinner="ratio: 0.5;">Saving </div>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  className="uk-button hms-btn uk-offcanvas-close"
                  type="button"
                >
                  Cancel
                </button>
              </div>
              <p className="secondary">
                When you add a unit, a home guide is automatically created for you. 
                You won’t be billed unless you subscribe to Digital Guestbooks.
              </p>
            </form>
            {/*
            {RentalsContext.logs?.map((log) => (
              <p>{utils.composeLog(log)}</p>
            ))}
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyGuide;
